import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import ModeEdit from "@mui/icons-material/ModeEdit";
import {
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  EpisodicEventItemFragment,
  EpisodicManifestExemptionState,
} from "generated-graphql/graphql";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { useState } from "react";

import { formatContainerDate } from "../Containers/containerDisplay";
import { AddEventManifestDialog } from "./AddEventManifestDialog";
import { EPISODIC_EVENT_MANIFESTS_QUERY, REMOVE_EPISODIC_MANIFEST_EXEMPTION_MUTATION } from "./api";
import { EXEMPTION_STATE_DISPLAY, formatPounds } from "./episodicDisplay";
import { describeAttachError } from "./episodicErrors";
import { EventSection } from "./EventSection";
import {
  EventManifestExemptions,
  ExemptManifest,
  ExemptStream,
  reliefAppliesToDeclarations,
  MANIFEST_REFETCH_QUERIES,
} from "./manifestExemptionForm";

type StreamRow = { manifest: ExemptManifest; stream: ExemptStream };

function streamRows(exemptions: EventManifestExemptions): StreamRow[] {
  return exemptions.manifests.flatMap((manifest) =>
    manifest.streams.map((stream) => ({ manifest, stream }))
  );
}

const isVanished = (stream: ExemptStream) =>
  stream.state === EpisodicManifestExemptionState.StreamNotOnManifest;

export function sectionCaption(exemptions: EventManifestExemptions): string {
  const counted = exemptions.manifests.reduce(
    (sum, manifest) => sum + manifest.streams.filter((stream) => !isVanished(stream)).length,
    0
  );
  const manifests = exemptions.manifests.filter((manifest) =>
    manifest.streams.some((stream) => !isVanished(stream))
  ).length;
  const gone = exemptions.manifests.length - manifests;
  const base = `${counted === 1 ? "1 waste stream" : `${counted} waste streams`} on ${
    manifests === 1 ? "1 manifest" : `${manifests} manifests`
  }`;
  const goneNote = gone > 0 ? ` (+${gone} no longer at this facility)` : "";
  return `${base}${goneNote}`;
}

/** The section subtitle: the excluded pounds first, then the EPA estimate and
 * filing chip when they exist, then the stream and manifest count. */
function SummaryLine({ exemptions }: { exemptions: EventManifestExemptions }) {
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {formatPounds(exemptions.totalExemptPounds)} excluded from generator status
        </Typography>
        {exemptions.epaEstimatedPounds != null && (
          <Typography variant="body2" color="text.secondary">
            · {formatPounds(exemptions.epaEstimatedPounds)} estimated on Form 8700-12
          </Typography>
        )}
        {exemptions.filedWithEpa && (
          <Chip size="small" color="success" variant="outlined" label="Filed with EPA" />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {sectionCaption(exemptions)}
      </Typography>
    </Stack>
  );
}

function StatusCell({ stream }: { stream: ExemptStream }) {
  const display = EXEMPTION_STATE_DISPLAY[stream.state];
  return (
    <TableCell>
      <Chip
        size="small"
        variant="outlined"
        color={display.applied ? "success" : "default"}
        label={display.label}
      />
      {display.detail && (
        <Typography variant="caption" color="text.secondary" component="div">
          {display.detail}
        </Typography>
      )}
    </TableCell>
  );
}

/** The exempt pounds when applied; otherwise a dash with the recorded pounds beneath, so they stay readable without Edit. */
function ExemptCell({ stream }: { stream: ExemptStream }) {
  const applied = EXEMPTION_STATE_DISPLAY[stream.state].applied;
  return (
    <TableCell align="right">
      {applied ? formatPounds(stream.exemptPounds) : "—"}
      {!applied && (
        <Typography variant="caption" color="text.secondary" component="div">
          declared {formatPounds(stream.exemption.declaredPounds)}
        </Typography>
      )}
    </TableCell>
  );
}

function ManifestStreamRow({
  row,
  readOnly,
  onEdit,
  onRemove,
}: {
  row: StreamRow;
  readOnly: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { manifest, stream } = row;
  const gone = stream.state === EpisodicManifestExemptionState.StreamNotOnManifest;
  return (
    <TableRow>
      <TableCell sx={{ color: gone ? "text.disabled" : undefined }}>
        {manifest.manifestTrackingNumber}
      </TableCell>
      <TableCell>{formatContainerDate(manifest.shippedDate)}</TableCell>
      <TableCell>
        {stream.wasteStream.name}
        {gone && (
          <Typography variant="caption" color="text.secondary" component="div">
            Re-ingested under another facility or removed; applies again if it returns.
          </Typography>
        )}
        {stream.changedSinceDeclared && (
          <Typography variant="caption" color="text.secondary" component="div">
            Waste details changed since you declared this. Review.
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{gone ? "—" : formatPounds(stream.groupPounds)}</TableCell>
      <ExemptCell stream={stream} />
      <StatusCell stream={stream} />
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {!gone && (
            // A disabled button fires no hover events, so each tooltip wraps a span.
            <Tooltip title="Edit amount">
              <span>
                <IconButton
                  size="small"
                  aria-label="Edit amount"
                  onClick={onEdit}
                  disabled={readOnly}
                >
                  <ModeEdit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Remove">
            <span>
              <IconButton
                size="small"
                color="error"
                aria-label="Remove"
                onClick={onRemove}
                disabled={readOnly}
              >
                <Delete fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function useRemoveExemption() {
  const alerts = useAlerts();
  const [removeExemption, { loading: removing }] = useMutation(
    REMOVE_EPISODIC_MANIFEST_EXEMPTION_MUTATION,
    { refetchQueries: MANIFEST_REFETCH_QUERIES }
  );
  return {
    removing,
    remove: (exemptionId: string, onSettled: () => void) =>
      removeExemption({
        variables: { exemptionId },
        onCompleted: () => {
          alerts.success("Declaration removed");
          onSettled();
        },
        onError: (error) => {
          alerts.error(describeAttachError(error, "The declaration could not be removed"));
          onSettled();
        },
      }),
  };
}

export function EventManifestsSection({
  event,
  collapsible = true,
}: {
  event: EpisodicEventItemFragment;
  /** Off when this is the card's only section, so there is nothing to fold away. */
  collapsible?: boolean;
}) {
  const { data, error } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, {
    variables: { id: event.id },
  });
  const exemptions = data?.episodicEvent?.manifestExemptions ?? null;
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [removeCandidate, setRemoveCandidate] = useState<{ id: string; label: string } | null>(
    null
  );
  const { isReadOnly } = useFacilityReadOnly(event.facilityId);
  const mutations = useRemoveExemption();

  const rows = exemptions ? streamRows(exemptions) : [];

  return (
    <EventSection
      title="Manifests"
      collapsible={collapsible}
      caption={exemptions ? <SummaryLine exemptions={exemptions} /> : "Loading manifests…"}
      actions={
        <Button
          size="small"
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setAddOpen(true)}
          disabled={!exemptions || isReadOnly}
        >
          Add a manifest
        </Button>
      }
    >
      {error && (
        <Typography variant="body2" color="error">
          The manifests could not be loaded.
        </Typography>
      )}
      {exemptions && (
        <Stack spacing={1.5}>
          {rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No manifests carry waste from this event yet. Pounds tied to this event leave the
              calculated generator status for the month they shipped.
            </Typography>
          )}
          {rows.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Manifest #</TableCell>
                    <TableCell>Ship date</TableCell>
                    <TableCell>Waste stream</TableCell>
                    <TableCell align="right">On manifest</TableCell>
                    <TableCell align="right">Exempt</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <ManifestStreamRow
                      key={`${row.manifest.manifestTrackingNumber}:${row.stream.wasteStream.id}`}
                      row={row}
                      readOnly={isReadOnly}
                      onEdit={() => setEditing(row.manifest.manifestTrackingNumber)}
                      onRemove={() =>
                        setRemoveCandidate({
                          id: row.stream.exemption.id,
                          label: `${row.stream.wasteStream.name} on ${row.manifest.manifestTrackingNumber}`,
                        })
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}

      {exemptions && (addOpen || editing !== null) && (
        <AddEventManifestDialog
          eventId={event.id}
          startDate={event.startDate}
          exemptions={exemptions}
          reliefApplies={reliefAppliesToDeclarations(event.relief)}
          initialManifestTrackingNumber={editing}
          open
          onClose={() => {
            setAddOpen(false);
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={removeCandidate != null}
        onClose={() => setRemoveCandidate(null)}
        onConfirm={() => {
          if (removeCandidate) {
            void mutations.remove(removeCandidate.id, () => setRemoveCandidate(null));
          }
        }}
        loading={mutations.removing}
        header={`Remove the declaration for ${removeCandidate?.label ?? "this stream"}?`}
        info="Its pounds count toward generator status again."
        confirmText="Remove"
      />
    </EventSection>
  );
}
